// Only what the fault baseline needs is imported statically — the crash reporter and
// the tracer. The behavioural instrumentations are loaded on demand from ./rum, since
// they must not run before consent.
//
// TracingInstrumentation is static despite carrying the OpenTelemetry web SDK, because
// it has to patch `fetch`/XHR before the shell issues its own first requests — see
// initTelemetry. Measured cost of that choice: the shell's entry bundle goes from
// 564,762 to 647,196 bytes raw, 173,424 to 198,220 gzipped — **+80 KB raw, +24 KB over
// the wire** on the critical path.
import {
  initializeFaro,
  ErrorsInstrumentation,
  FetchTransport,
  LogLevel,
  genShortID,
  type Faro,
} from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import { loadRumInstrumentations } from './rum'
import {
  APP_NAME,
  APP_VERSION,
  COLLECTOR_URL,
  ENVIRONMENT,
  TRACE_PROPAGATE_TO,
  isTelemetrySupported,
} from './config'
import type { TelemetryAttributes, TelemetryValues } from './types'

/**
 * Grafana Faro, in two stages.
 *
 * The app has two very different obligations here, and they do not start at the same
 * moment. The line between them is **fault versus audience**, not "how much data":
 *
 *  1. **Faults, from first paint.** A crash on load, or a request that took eight
 *     seconds and then 500'd, is a fault in software the user asked to run; collecting
 *     it is legitimate interest, not audience measurement. It also has to work
 *     *before* the cookie banner is answered, because the failures worth catching are
 *     exactly the ones that happen before anyone clicks anything. {@link initTelemetry}
 *     sets this up: unhandled errors, plus distributed traces for fetch/XHR — and no
 *     session identifier, no persisted storage, no behavioural signals.
 *
 *     A trace is diagnostics: it says a request was slow, not who made it. Nothing in
 *     this stage is written to the device and nothing identifies the visitor, so the
 *     spans it emits carry app/version/environment/page and no more (Faro's span
 *     processor stamps `session.id`/`user.*` only when those are set, and both are
 *     gated below).
 *
 *  2. **RUM, on consent.** Web vitals, view/route tracking, resource timings and above
 *     all a **session id** are audience measurement — they profile how one visitor
 *     moved through the app, and the id is storage on the visitor's device. None of it
 *     starts until the visitor grants the `analytics` category, at which point
 *     {@link enableRumTelemetry} bolts the remaining instrumentations onto the
 *     already-running instance — and the session id, arriving there, begins appearing
 *     on the spans stage 1 was already emitting.
 *
 * Consent is not revocable within a load, exactly as in `consent/consentLoaders.ts`:
 * instrumentations that patch `fetch`/`history` cannot be cleanly un-patched, so a
 * withdrawal takes effect on the next full load (nothing is re-enabled). What *is*
 * honoured immediately is the gate below — {@link rumEnabled} — so pilet events stop
 * being accepted the moment consent is gone.
 */

/** The single instance. Undefined until {@link initTelemetry}, and if it stays that
 *  way (unsupported environment, failed init) every function here is a no-op. */
let faro: Faro | undefined

/** Whether the `analytics` category has been granted this load — the RUM gate. */
let rumEnabled = false

/**
 * The tenant this deployment serves, once whoami has answered (see
 * {@link setTelemetryTenant}). Read through a meta *getter* registered at init rather
 * than written into the config, because it is not known when Faro starts.
 */
let tenant: { tenant_id: string, tenant_hostname: string } | undefined

/**
 * Faro's transport takes `Record<string, string>`; our callers pass numbers and
 * booleans because that is what the data actually is. Coerce here rather than making
 * every call site stringify, and drop null/undefined outright — an attribute whose
 * value is the string "undefined" is worse than an absent one, because it looks like
 * a real cardinality bucket in a Grafana query.
 */
function toStringAttributes (attributes?: TelemetryAttributes): Record<string, string> | undefined {
  if (!attributes) return undefined
  const result: Record<string, string> = {}
  Object.keys(attributes).forEach(key => {
    const value = attributes[key]
    if (value === undefined || value === null) return
    result[key] = String(value)
  })
  return result
}

/**
 * Strip the query string off a URL.
 *
 * The watch page keeps the playing stream and the playback position in the address
 * bar (`?s`/`?t`, rewritten every few seconds), and search puts the typed query
 * there. None of it should reach the collector: `?t` would turn the page URL into a
 * per-second unique value — useless as a dimension and a small behavioural log of one
 * viewer — and a search term is the visitor's own words. The path alone is what
 * identifies the page.
 */
function stripQuery (url?: string): string | undefined {
  if (!url) return url
  const cut = url.indexOf('?')
  return cut === -1 ? url : url.slice(0, cut)
}

/**
 * The last thing that happens to every signal: stamp the tenant on it and strip the
 * query strings out of it.
 *
 * **Why here and not in `metas`.** Faro's config takes meta *getters*, which would be
 * the obvious home for a value discovered after init — but `Metas.value` merges its
 * items with a shallow `Object.assign`, so a getter returning `{ page: {...} }`
 * replaces the whole `page` meta rather than extending it, taking `page.url` with it.
 * Same for `{ app: {...} }` and the app's name and version. A `beforeSend` hook runs
 * on the already-assembled meta, so it can *add* to those objects instead of
 * substituting for them.
 *
 * **Why it copies rather than mutates.** `metas.value` is a shallow merge, so
 * `item.meta.page` is the very object the page meta item holds — not a copy. Writing
 * through it would edit Faro's own shared state from a send hook, which is both a
 * surprising place for that to happen and order-dependent. Everything touched here is
 * rebuilt.
 *
 * The tenant lands in two places on purpose:
 *  - `app.namespace` — a first-class Faro field, so it becomes a real label at the
 *    collector that Alloy routing rules and Grafana dashboard variables select on
 *    directly, with no attribute digging.
 *  - `page.attributes` — carries the opaque `tenant_id` alongside the hostname.
 *    `page` is the only always-present meta member with free-form attributes;
 *    `session` and `user` exist only after consent, which is precisely when the
 *    tenant must not go missing.
 */
function decorate<T extends { meta?: any }> (item: T): T {
  const meta = item.meta
  if (!meta) return item

  const page = meta.page ?? {}
  const app = meta.app ?? {}

  return {
    ...item,
    meta: {
      ...meta,
      app: tenant ? { ...app, namespace: tenant.tenant_hostname } : app,
      page: {
        ...page,
        url: stripQuery(page.url),
        attributes: tenant
          ? { ...page.attributes, tenant_id: tenant.tenant_id, tenant_hostname: tenant.tenant_hostname }
          : page.attributes,
      },
      // `MetaView.name` is required, so this only rebuilds the object when there is
      // one — an absent view must stay absent rather than become `{ name: undefined }`.
      view: meta.view ? { ...meta.view, name: stripQuery(meta.view.name) ?? meta.view.name } : meta.view,
    },
  }
}

/**
 * Start Faro in its fault-reporting baseline. Call once, as early as possible in the
 * shell's startup — before React renders, so a crash during first render is caught,
 * and **before the shell issues any request of its own**, which is what makes the
 * tracer worth having here at all (see below).
 *
 * Deliberately minimal:
 *  - `sessionTracking.enabled: false` — no session id is minted and nothing is
 *    written to storage, so this stage sets no identifier of any kind. The id
 *    arrives with consent (see {@link enableRumTelemetry}).
 *  - {@link ErrorsInstrumentation} and {@link TracingInstrumentation} only — no web
 *    vitals, no view tracking, no console capture, no session.
 *  - `beforeSend` drops query strings from every URL it can reach.
 *
 * Never throws: a failure to initialise observability must not take down the app it
 * was meant to observe.
 */
export function initTelemetry (): void {
  if (faro || !isTelemetrySupported()) return

  try {
    faro = initializeFaro({
      transports: [new FetchTransport({ url: COLLECTOR_URL })],
      app: {
        name: APP_NAME,
        version: APP_VERSION,
        environment: ENVIRONMENT,
      },
      // Pre-consent: no identifier, no storage. `persistent: false` is belt and
      // braces — with `enabled: false` no manager runs at all.
      sessionTracking: { enabled: false, persistent: false },
      // The crash reporter and the tracer. Everything behavioural is added on consent.
      //
      // Tracing is here, ahead of the gate, because request tracing is how the service
      // is kept working rather than how its audience is measured — and because it is
      // only useful if it is installed *first*. The shell's own startup calls (whoami
      // at index.tsx, the domain configuration fetched eagerly by `createConfigApi`,
      // the pilet feed) all fire within the same synchronous block as this init; an
      // instrumentation loaded a chunk-download later patches `fetch` long after they
      // are in flight, and those requests — the ones whose latency decides whether the
      // app starts at all — are precisely the ones that would never be traced.
      //
      // Scoped to our own origin, which is where the content API lives; see
      // TRACE_PROPAGATE_TO for why the player CDN is deliberately excluded.
      instrumentations: [
        new ErrorsInstrumentation(),
        new TracingInstrumentation({
          instrumentationOptions: { propagateTraceHeaderCorsUrls: TRACE_PROPAGATE_TO },
        }),
      ],
      // Noise that tells us nothing about our own code: a browser extension's
      // injected script, and the two benign ResizeObserver warnings every app with a
      // resize-driven layout emits.
      ignoreErrors: [
        /ResizeObserver loop/,
        /^Script error\.?$/,
        /extension:\/\//,
      ],
      // Never observe the beacon itself. Without this the tracer would span every POST
      // to the collector and report *that* to the collector — a feedback loop that
      // grows with itself. It matters more now that tracing starts at init rather than
      // on consent: the loop would open on the first load, for every visitor.
      ignoreUrls: [COLLECTOR_URL],
      // Console capture is added with the rest of RUM on consent, but its settings
      // live on the config, so they have to be declared here at init.
      //
      // Warnings and errors only. `console.log`/`debug`/`info` stay out on purpose:
      // this codebase logs raw API failures through them (the watch pilet's
      // `console.error(err)` sites, the consent module's debug line), and those
      // payloads are not ours to ship. `serializeErrors` is left off for the same
      // reason — the message and stack are what debugging needs, not the object graph
      // hanging off the error.
      consoleInstrumentation: {
        disabledLevels: [LogLevel.DEBUG, LogLevel.LOG, LogLevel.INFO, LogLevel.TRACE],
      },
      beforeSend: decorate,
    })
  } catch (err) {
    // Nothing to report it *to* — the reporter is what just failed.
    console.warn('[telemetry] Faro initialisation failed', err)
    faro = undefined
  }
}

/**
 * Open the RUM stage. Called by `consent/consentLoaders.ts` once the visitor has
 * granted `analytics` — on this load or on a previous one, since a stored decision is
 * re-applied at startup.
 *
 * Idempotent: consent is broadcast on the window event *and* re-applied from storage,
 * so this runs more than once per load. The flag is flipped before the await, so the
 * two calls cannot both start a download; Faro's `instrumentations.add` additionally
 * refuses duplicates by name.
 *
 * Returns immediately — the instrumentation chunk is fetched in the background. The
 * caller has nothing to wait for: the gate below opens at once, so pilet events start
 * being recorded while the chunk is still on its way, and everything the
 * instrumentations collect (vitals, timings) is read from browser buffers that
 * outlive their arrival. Tracing is exempt from that reasoning, which is exactly why
 * it does *not* live here: a `fetch` that already happened leaves no buffer to read.
 */
export function enableRumTelemetry (): void {
  if (!faro || rumEnabled) return
  rumEnabled = true

  // A session id, now that we may have one. Set synchronously, before the chunk
  // lands, so the events accepted in the meantime already carry it. Minted per load
  // and never persisted — enough to stitch one visit's signals together (which is
  // what makes "this error happened during that view" answerable), not enough to
  // recognise the visitor on their next visit.
  try {
    faro.api.setSession({ id: genShortID() })
  } catch (err) {
    console.warn('[telemetry] starting the session failed', err)
  }

  loadRumInstrumentations()
    .then(instrumentations => faro?.instrumentations.add(...instrumentations))
    // A failed chunk fetch (offline, a stale deploy) costs us the behavioural
    // signals; the error reporter and the pilet event API carry on regardless.
    .catch(err => console.warn('[telemetry] enabling RUM failed', err))
}

/**
 * Name the tenant this deployment serves. Called at shell startup, as soon as whoami
 * answers (see `auth/whoami.ts`) — on the anonymous 403 as much as on the signed-in
 * 200, because the `domain` block is on both.
 *
 * **Not consent-gated.** The tenant identifies the *site*, not the visitor: it is the
 * same value for every person on that hostname and reveals nothing about any of them.
 * Gating it would make the error reports that arrive before a consent decision — the
 * startup crashes, the ones worth having — impossible to attribute to a library, which
 * is the difference between "medialib is broken" and "this one deployment is broken".
 *
 * Applies to every signal sent *after* this call. The handful of errors that can fire
 * in the window before whoami answers arrive without a tenant; that window is why the
 * whoami request is started at startup rather than from a React effect.
 */
export function setTelemetryTenant (identity: { tenant_id: string, tenant_hostname: string } | null): void {
  if (!identity?.tenant_id) return
  tenant = identity
}

/** Whether behavioural telemetry may be recorded — the gate every event goes through. */
export function isRumEnabled (): boolean {
  return !!faro && rumEnabled
}

/**
 * Record a discrete event. Dropped unless RUM is on — see {@link PiletTelemetryApi}.
 */
export function trackEvent (name: string, attributes?: TelemetryAttributes, domain?: string): void {
  if (!isRumEnabled()) return
  try {
    faro?.api.pushEvent(name, toStringAttributes(attributes), domain)
  } catch (err) {
    console.warn('[telemetry] pushEvent failed', err)
  }
}

/**
 * Record a numeric measurement. Dropped unless RUM is on.
 */
export function trackMeasurement (type: string, values: TelemetryValues, attributes?: TelemetryAttributes): void {
  if (!isRumEnabled()) return
  try {
    faro?.api.pushMeasurement({ type, values }, { context: toStringAttributes(attributes) })
  } catch (err) {
    console.warn('[telemetry] pushMeasurement failed', err)
  }
}

/**
 * Report a handled error. NOT consent-gated — this is the baseline stage's whole
 * purpose, so it only needs Faro to exist.
 *
 * Accepts `unknown` because that is what a `catch` binding is: a non-Error rejection
 * value (a string, a status code) is wrapped rather than dropped, so a `throw 403`
 * still arrives as something with a message.
 */
export function trackError (error: unknown, context?: TelemetryAttributes): void {
  if (!faro) return
  try {
    const err = error instanceof Error ? error : new Error(String(error))
    faro.api.pushError(err, { context: toStringAttributes(context) })
  } catch (err) {
    console.warn('[telemetry] pushError failed', err)
  }
}

/**
 * Name the view the visitor is on — the shell calls this on every route change (see
 * useViewTracking). Views are a RUM signal, so this is gated too; the automatic
 * {@link ViewInstrumentation} added on consent covers the same ground, but this is
 * what gives a view the *route pattern* (`/watch/:media_type/:video_id`) rather than
 * the URL, which is the only form that aggregates.
 */
export function setTelemetryView (name: string): void {
  if (!isRumEnabled()) return
  try {
    faro?.api.setView({ name })
  } catch (err) {
    console.warn('[telemetry] setView failed', err)
  }
}

/**
 * Attribute signals to this **browser**, so "one visitor hit this 40 times" can be
 * told apart from "40 visitors hit this once" — the difference between a papercut and
 * an outage, and not something a session id alone can answer across reloads.
 *
 * The identifier is the backend-generated *device* id from enrollment (see
 * `auth/device.ts`), not the account: it is opaque, already stored by the app for its
 * own purposes, and carries no name, login or email. `whoami`'s profile deliberately
 * stays out of telemetry — `username` is personal data, and nothing in a crash report
 * is improved by knowing it.
 *
 * Gated on consent, unlike {@link trackError} itself: an unattributed error report is
 * still a useful crash report, and a stable cross-load identifier is exactly the kind
 * of thing the `analytics` category exists to authorise.
 */
export function setTelemetryUser (id: string | null): void {
  if (!isRumEnabled()) return
  try {
    if (id) faro?.api.setUser({ id })
    else faro?.api.resetUser()
  } catch (err) {
    console.warn('[telemetry] setUser failed', err)
  }
}
